import type {AssetUploadProgress} from '../assets/assets';
import type {RemotionEditorApi} from '../api-context';
import type {UploadResult} from './asset-upload-utils';
import {uploadWithProgress} from './upload';

export type PresignResult =
	| {
			type: 'success';
			result: UploadResult;
	  }
	| {
			type: 'no-credentials';
	  }
	| {
			type: 'file-too-large';
	  }
	| {
			type: 'error';
			error: unknown;
	  };

/**
 * Request presigned upload URLs from the Obelisk backend.
 *
 * Uses POST /v1/remotion/upload via the authenticated API client.
 * Maps the snake_case backend response to the camelCase UploadResult
 * expected by the rest of the editor code.
 */
export const getUploadUrls = async (
	file: Blob,
	api: RemotionEditorApi,
): Promise<PresignResult> => {
	try {
		const response = await api.getPresignedUploadUrl({
			content_type: file.type,
			file_size: file.size,
		});

		// Map snake_case backend response → camelCase UploadResult
		const result: UploadResult = {
			presignedUrl: response.presigned_url,
			readUrl: response.read_url,
			fileKey: response.storage_key,
		};

		return {type: 'success', result};
	} catch (err: unknown) {
		// The AuthenticatedApiClient throws ApiError with status codes
		const status = (err as {status?: number}).status;

		if (status === 404) {
			return {type: 'no-credentials'};
		}

		if (status === 413) {
			return {type: 'file-too-large'};
		}

		return {type: 'error', error: err};
	}
};

export const uploadWithProgressUpdates = async (
	file: Blob,
	presignedUrl: string,
	onProgress: (progress: AssetUploadProgress) => void,
): Promise<void> => {
	await uploadWithProgress({
		file,
		url: presignedUrl,
		onProgress,
	});
};
